import { KeyboardArrowUp } from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  Drawer,
  IconButton,
  List,
  ListItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PortnetLogo } from "../../../../asset/images/LandingPage";
import PTTranslate from "../../../../common/components/ui/PTTranslate";
import { useAuth } from "../../../../common/hooks/useAuth";
import { LanguegeSelect } from "../../../../common/navbar/components/FirstHeader";
import CloseIcon from '@mui/icons-material/Close';
import ContactPage from "../../page/ContactPage";
import ContactDialog from "./ContactDialog";

const Navbar = ({ onLoginClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { isAuthenticated, logout } = useAuth();
  const [activeItem, setActiveItem] = useState("about");
  const [hoverItem, setHoverItem] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  // Add breakpoint for medium-small screens
  const isMediumSmall = useMediaQuery('(min-width:768px) and (max-width:1100px)');
  
  // Close mobile menu on screen resize
  useEffect(() => {
    const handleResize = () => {
      if (!isMobile && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile, mobileMenuOpen]);

  // Toggle mobile menu based on screen size
  useEffect(() => {
    // If screen width is less than 986px or greater than 1100px, use the default mobile/desktop breakpoints
    // But if we're in that problematic middle range (986-1100px), force mobile menu
    const checkMiddleBreakpoint = () => {
      const width = window.innerWidth;
      if (width >= 986 && width <= 1100) {
        // The problematic range where navbar elements stack
        if (!isMobile) {
          // Only show mobile menu in this specific range if we weren't already in mobile mode
          setMobileMenuOpen(true);
        }
      }
    };
    
    // Run once on component mount
    checkMiddleBreakpoint();
    
    // No need for event listener as the useMediaQuery hooks will handle responsiveness
  }, []);

  const handleLogoClick = () => {
    navigate("/");
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  // Handle opening contact dialog
  const handleContactClick = () => {
    setContactDialogOpen(true);
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  // Handle closing contact dialog
  const handleCloseContactDialog = () => {
    setContactDialogOpen(false);
  };

  // Updated to handle scroll to section
  const handleNavItemClick = (sectionId) => {
    if (sectionId === "contact") {
      handleContactClick();
      return;
    }
    
    if (sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        // Smooth scroll to element
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveItem(sectionId);
      } else if (sectionId.startsWith('http')) {
        // Handle external URLs
        window.location.href = sectionId;
      }
    }
    
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  const handleMouseEnter = (item) => {
    if (isMobile) return;
    setHoverItem(item);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    setHoverItem(null);
  };

  const handleExpandItem = (item) => {
    setExpandedItem(expandedItem === item ? null : item);
  };

  const handleAuthButton = () => {
    if (isAuthenticated) {
      logout();
    } else {
      onLoginClick();
    }
    
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  const NavButton = ({ children, id, sectionId }) => (
    <Button
      onMouseEnter={() => handleMouseEnter(id)}
      onMouseLeave={() => handleMouseLeave()}
      onClick={() => handleNavItemClick(sectionId)}
      disableRipple
      sx={{
        color: "#fff",
        fontSize: { md: "0.75rem", lg: "0.875rem", xl: "1rem" },
        fontWeight: 500,
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: -1,
          left: 8,
          right: 8,
          height: "3px",
          backgroundColor: "#4CAF50",
          opacity: activeItem === id || hoverItem === id ? 1 : 0,
          transition: "opacity 0.3s",
        },
        "&:hover::after": {
          opacity: 1,
        },
        marginRight: { md: 0.5, lg: 1 },
        padding: { md: "6px 4px", lg: "6px 8px", xl: "8px 16px" },
        minWidth: "auto", // Allow buttons to be smaller
      }}
    >
      {children}
    </Button>
  );

  return (
    <>
      <AppBar
        id="app-bar"
        position="absolute"
        elevation={0}
        sx={{
          background: "transparent",
          boxShadow: "none",
          pt: { xs: 1, sm: 2, md: 2, lg: 3 },
          width: "100%",
          zIndex: 1100,
        }}
      >
        <Container 
          maxWidth={false} 
          sx={{ 
            px: { xs: 2, sm: 3, md: 3, lg: 6, xl: 10 },
            maxWidth: "100%"
          }}
        >
          <Toolbar 
            sx={{ 
              justifyContent: "space-between", 
              p: 0,
              minHeight: { xs: "56px", sm: "64px" }
            }}
          >
            <IconButton
              onClick={handleLogoClick}
              disableRipple
              sx={{
                p: 0,
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <Box
                component="img"
                src={PortnetLogo}
                alt="PORTNET"
                sx={{
                  width: { xs: "120px", sm: "auto" },
                  height: "auto",
                  cursor: "pointer",
                }}
              />
            </IconButton>

            {/* Desktop Navigation */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: { md: 0.5, lg: 1 },
                flexWrap: "nowrap", // Prevent wrapping at middle screen sizes
              }}
            >
              <NavButton id="about" sectionId="about-section">
                <PTTranslate tk="navbar.about" />
              </NavButton>
              <NavButton id="how" sectionId="how-it-works">
                <PTTranslate tk="navbar.how" />
              </NavButton>
              <NavButton id="contact" sectionId="contact">
                <PTTranslate tk="navbar.contact" />
              </NavButton>

              <Button
                variant="contained"
                disableRipple
                onClick={handleAuthButton}
                sx={{
                  backgroundColor: "#fff",
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                  textTransform: "none",
                  minWidth: { md: "80px", lg: "120px" },
                  maxWidth: { md: "120px", lg: "160px" },
                  whiteSpace: "nowrap",
                  px: { md: 1, lg: 3 },
                  py: { md: 0.5, lg: 1 },
                  ml: { md: 1, lg: 2 },
                  fontWeight: 600,
                  borderRadius: "9px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  fontSize: { md: "0.75rem", lg: "0.875rem", xl: "1rem" },
                }}
              >
                {isAuthenticated ? <PTTranslate tk="navbar.logout" /> : <PTTranslate tk="navbar.login" />}
              </Button>
              
              <Box sx={{ ml: { md: 1, lg: 2 } }}>
                <LanguegeSelect
                  sx={{
                    backgroundColor: "transparent",
                    color: "#fff",
                    fontSize: { md: "0.75rem", lg: "0.875rem" },
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      color: "#fff",
                    },
                    p: { md: 0.25, lg: 0.5 },
                  }}
                />
              </Box>
            </Box>

            {/* Mobile Right Section */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                gap: 1,
              }}
            >
              {/* Mobile Language Selector */}
              <Box sx={{ display: { xs: "flex", sm: "flex" } }}>
                <LanguegeSelect
                  sx={{
                    backgroundColor: "transparent",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      color: "#fff",
                    },
                    p: 0.5,
                  }}
                />
              </Box>
              
              {/* Mobile Menu Button */}
              <IconButton
                disableRipple
                sx={{
                  color: "#fff",
                  display: { xs: "flex", md: "none" },
                }}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <MenuIcon />
              </IconButton>
            </Box>

            {/* Mobile Menu Drawer */}
            <Drawer
              anchor="right"
              open={mobileMenuOpen}
              onClose={() => setMobileMenuOpen(false)}
              sx={{
                "& .MuiDrawer-paper": {
                  width: { xs: "85%", sm: "60%" },
                  maxWidth: "300px",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  backdropFilter: "blur(10px)",
                  color: "#fff",
                },
              }}
            >
              <Box sx={{ p: 3 }}>
                <List>
                  <ListItem
                    button
                    disableRipple
                    onClick={() => handleNavItemClick("about-section")}
                    sx={{
                      py: 1.5,
                      borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, color: "rgb(255, 255, 255)" }}>
                      <PTTranslate tk="navbar.about" />
                    </Typography>
                  </ListItem>

                  <ListItem
                    button
                    disableRipple
                    onClick={() => handleNavItemClick("how-it-works")}
                    sx={{
                      py: 1.5,
                      borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, color: "rgb(255, 255, 255)" }}>
                      <PTTranslate tk="navbar.how" />
                    </Typography>
                  </ListItem>

                  <ListItem
                    button
                    disableRipple
                    onClick={() => handleNavItemClick("contact")}
                    sx={{
                      py: 1.5,
                      borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, color: "rgb(255, 255, 255)" }}>
                      <PTTranslate tk="navbar.contact" />
                    </Typography>
                  </ListItem>

                  <ListItem sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      disableRipple
                      onClick={handleAuthButton}
                      sx={{
                        backgroundColor: "#fff",
                        color: theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "9px",
                        py: 1,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    >
                      {isAuthenticated ? <PTTranslate tk="navbar.logout" /> : <PTTranslate tk="navbar.login" />}
                    </Button>
                  </ListItem>
                </List>
              </Box>
            </Drawer>
          </Toolbar>
        </Container>
      </AppBar>

      <ContactDialog
        open={contactDialogOpen}
        onClose={handleCloseContactDialog}
      />
    </>
  );
};

export default Navbar;